import React, { useState, useMemo, useContext } from 'react'
import { Select, ConfigProvider } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'

const { Option } = Select

enum PagePrevOrNext {
  prev = 'prev',
  next = 'next',
}

interface PrevOrNextPageTypes {
  page: number | PagePrevOrNext
  currentPage: number
  updateCurrentPage: any
  totalPage: number
  antPrefixCls: string
}

// 渲染上一页 / 下一页 按钮
function PrevOrNextPage(props: PrevOrNextPageTypes) {
  const { currentPage, page, updateCurrentPage, totalPage, antPrefixCls } = props

  if (page === PagePrevOrNext.prev) {
    const isDisabled = currentPage <= 1
    const isDisabledClassName = isDisabled ? ` ${antPrefixCls}-pagination-disabled` : ''
    const handlerClick = isDisabled ? () => {} : () => updateCurrentPage(currentPage - 1)

    return (
      <li title="上一页" className={`${antPrefixCls}-pagination-prev ` + isDisabledClassName}>
        <button onClick={handlerClick} className={`${antPrefixCls}-pagination-item-link`} type="button">
          <LeftOutlined />
        </button>
      </li>
    )
  } else {
    const isDisabled = currentPage >= totalPage
    const isDisabledClassName = isDisabled ? ` ${antPrefixCls}-pagination-disabled` : ''
    const handlerClick = isDisabled ? () => {} : () => updateCurrentPage(currentPage + 1)

    return (
      <li title="下一页" className={`${antPrefixCls}-pagination-next ` + isDisabledClassName}>
        <button onClick={handlerClick} className={`${antPrefixCls}-pagination-item-link`} type="button">
          <RightOutlined />
        </button>
      </li>
    )
  }
}

interface AcorePaginationType {
  defaultCurrent?: number
  total: number
  defaultPageSize?: number
  showSizeChanger?: boolean
  onChange?: any
  onShowSizeChange?: any
  current?: number
  className?: string
  pageSizeOptions?: Array<number>
  showTotal?: any
}

/**
 *
 * 要根据一共有多少页
 * @param props
 */
function AcorePagination(props: AcorePaginationType) {
  const antConfig = useContext(ConfigProvider.ConfigContext)
  const {
    total = 0,
    defaultPageSize = 10,
    defaultCurrent = 1,
    showSizeChanger,
    onChange = () => {},
    onShowSizeChange = () => {},
    current,
    className = '',
    pageSizeOptions = [10, 20, 50, 100],
    showTotal, // = (current: number, range: number) => null
  } = props

  const antPrefixCls = antConfig.getPrefixCls()

  const [pageSize, setPageSize] = useState(defaultPageSize)

  // 总页数
  const totalPage = Math.ceil(total / pageSize)

  // 非受控时用到的currentPage
  const [innerCurrentPage, setInnerCurrrentPage] = useState(() => {
    // current >= 1
    if (defaultCurrent < 1) return 1

    if (defaultCurrent > totalPage) return totalPage

    return defaultCurrent
  })

  // 是否是受控组件
  const isControlled = current != null
  const currentPage = isControlled ? (current as number) : innerCurrentPage
  const updateCurrentPage = isControlled ? onChange : innerUpdateCurrentPage

  function innerUpdateCurrentPage(nextPage: number, pageSize: number) {
    setInnerCurrrentPage(nextPage)
    onChange(nextPage, pageSize)
  }

  function handlerClickPageBtn(current: number | string) {
    let nextPage
    if (typeof current === 'string') {
      if (current === 'prev') {
        nextPage = currentPage - 1
      } else {
        nextPage = currentPage + 1
      }
    } else {
      nextPage = current
    }

    updateCurrentPage(nextPage, pageSize)
  }

  // 计算按钮数组 currentPage: 8 ['prev', 5, 6, 7, 8, 9, 10, 1, 'next'1
  const pagenitions = useMemo(() => {
    const pagenitions = caclPageBtns(totalPage, currentPage, 6)

    pagenitions.unshift(PagePrevOrNext.prev)
    pagenitions.push(PagePrevOrNext.next)

    return pagenitions
  }, [totalPage, currentPage])

  // TODO: 不能随意请求，修改行为为：changePageSize currentPage = 1
  function changePageSize(value: number) {
    updateCurrentPage(1, value)

    setPageSize(value)
    onShowSizeChange(1, value)
  }

  if (showTotal && typeof showTotal !== 'function')
    throw new Error(`showTotal 必须是返回ReactNode的函数，现在是: ` + showTotal)

  const innerShowSizeChanger = showSizeChanger == null ? total >= 50 : showSizeChanger

  return (
    <ul className={`${antPrefixCls}-pagination ` + className}>
      {showTotal && (
        <li className={`${antPrefixCls}-pagination-total-text`}>
          {showTotal(total, [Math.max(0, (currentPage - 1) * pageSize + 1), Math.min(total, currentPage * pageSize)])}
        </li>
      )}
      {pagenitions.map((item) => {
        if (typeof item === 'string') {
          return (
            <PrevOrNextPage
              key={item}
              currentPage={currentPage}
              page={item}
              antPrefixCls={antPrefixCls}
              updateCurrentPage={handlerClickPageBtn}
              totalPage={totalPage}
            />
          )
        }
        return (
          <li
            key={item}
            // 大年页不能点击
            onClick={item === currentPage ? undefined : () => handlerClickPageBtn(item)}
            className={
              `${antPrefixCls}-pagination-item ` +
              (item === currentPage ? `${antPrefixCls}-pagination-item-active` : '')
            }
          >
            <a rel="nofollow">{item}</a>
          </li>
        )
      })}
      {innerShowSizeChanger ? (
        <li className={`${antPrefixCls}-pagination-options`}>
          <SizeChanger
            antPrefixCls={antPrefixCls}
            pageSizeOptions={pageSizeOptions}
            pageSize={pageSize}
            onChange={changePageSize}
          />
        </li>
      ) : null}
    </ul>
  )
}

interface SizeChangerTypes {
  onChange?: any
  pageSize?: number
  pageSizeOptions?: Array<number>
  antPrefixCls?: string
}

function SizeChanger(props: SizeChangerTypes) {
  const { onChange, pageSize = 10, pageSizeOptions = [], antPrefixCls } = props

  // 如果修改pageSizeOptions那么当前pageSize一定再options中
  const options = useMemo(() => {
    let _options = []

    let hasPageSize = pageSizeOptions.indexOf(pageSize) !== -1

    if (hasPageSize) return pageSizeOptions

    for (var i = 0, len = pageSizeOptions.length; i < len; i++) {
      if (!hasPageSize && pageSizeOptions[i] > pageSize) {
        _options.push(pageSize)
        hasPageSize = true
      }
      _options.push(pageSizeOptions[i])
    }

    if (!hasPageSize) _options.push(pageSize)

    return _options
  }, [pageSizeOptions])

  return (
    <div
      className={`${antPrefixCls}-select ${antPrefixCls}-pagination-options-size-changer ${antPrefixCls}-select-single ${antPrefixCls}-select-show-arrow`}
    >
      <Select onChange={onChange} defaultValue={pageSize}>
        {options?.map((value) => (
          <Option key={value} value={value}>
            {value}条/页
          </Option>
        ))}
      </Select>
    </div>
  )
}

// 计算分页器
function caclPageBtns(
  // 共有多少页
  totalPage: number,
  currentPage: number,
  // 当前页，左边和右边页数
  leftAndRightPages: number
): Array<number | PagePrevOrNext> {
  // 所有按钮数要小于等于总页数
  // leftAndRightPages + 1 <= totalPage
  if (leftAndRightPages + 1 > totalPage) {
    leftAndRightPages = totalPage - 1
  }

  const pageBtns = [currentPage]
  let leftPage = currentPage - 1
  let rightPage = currentPage + 1

  while (true) {
    if (leftAndRightPages > 0 && leftPage > 0) {
      pageBtns.unshift(leftPage)
      leftPage--
      leftAndRightPages--
    }

    if (leftAndRightPages > 0 && rightPage <= totalPage) {
      pageBtns.push(rightPage)
      rightPage++
      leftAndRightPages--
    }

    if (leftAndRightPages <= 0) break
  }

  return pageBtns
}

export default AcorePagination
// export default function Abc() {
//   return <AcorePagination defaultCurrent={2} total={100} />;
// }
